export interface AutomationRunSummary {
  status: string;
  errorMessage?: string | null;
}

export interface WaitForAutomationRunOptions {
  load: (runId: string) => Promise<AutomationRunSummary>;
  sleep?: (milliseconds: number) => Promise<void>;
  pollIntervalMs?: number;
  timeoutMs?: number;
  signal?: AbortSignal;
}

const TERMINAL_STATUSES = new Set(["Completed", "Failed", "Cancelled"]);

function abortError() {
  const error = new Error("PDF report generation was cancelled.");
  error.name = "AbortError";
  return error;
}

export async function waitForAutomationRun(
  runId: string,
  options: WaitForAutomationRunOptions,
): Promise<AutomationRunSummary> {
  const pollIntervalMs = options.pollIntervalMs ?? 1_000;
  const timeoutMs = options.timeoutMs ?? 300_000;
  const sleep = options.sleep ?? ((milliseconds) =>
    new Promise<void>((resolve) => setTimeout(resolve, milliseconds)));
  const startedAt = Date.now();

  while (true) {
    if (options.signal?.aborted) throw abortError();

    const run = await options.load(runId);
    if (TERMINAL_STATUSES.has(run.status)) return run;

    if (Date.now() - startedAt >= timeoutMs) {
      throw new Error("PDF report generation timed out. Please try again.");
    }

    await sleep(pollIntervalMs);
  }
}

export function assertAutomationRunCompleted(run: AutomationRunSummary) {
  if (run.status === "Completed") return;

  if (run.status === "Cancelled") {
    throw new Error("PDF report generation was cancelled.");
  }

  throw new Error(
    run.errorMessage?.trim()
      || "PDF report generation failed. Please try again.",
  );
}

export function resolvePdfDownloadFileName(
  configuredFileName: string | null | undefined,
  reportPath: string,
) {
  const configured = configuredFileName?.trim();
  const pathName = reportPath.split(/[?#]/, 1)[0].split("/").filter(Boolean).pop();
  const baseName = configured || pathName || "report.pdf";
  return baseName.toLowerCase().endsWith(".pdf")
    ? baseName
    : `${baseName}.pdf`;
}
